/**
 * Pure workspace operations + defensive normalizers for the Coral Placer's
 * persisted state (aquascapes, placements, coral edits). All functions are
 * immutable and React/DOM-free so they can be unit-tested directly and
 * reused by the canvas UI. The hooks that wire these to the Pi live in
 * `persistence.ts`.
 */
import { isColorTag, isGrowthForm } from './catalog';
import type {
  Aquascape,
  ColorTag,
  CoralEdit,
  CoralEdits,
  GrowthForm,
  Placement,
  PlacementPhoto,
  Workspace,
} from './types';

export const DEFAULT_DOT_SIZE = 0.05;
export const MIN_DOT_SIZE = 0.02;
export const MAX_DOT_SIZE = 0.16;

// The Coral Placer ships with no bundled sample aquascape. The operator
// starts from a blank workspace and uploads their own rockwork photo, so
// the canvas only ever shows real tank photos.
export const DEFAULT_WORKSPACE: Workspace = {
  version: 1,
  aquascapes: [],
  activeAquascapeId: '',
  placements: {},
  defaultDotSize: DEFAULT_DOT_SIZE,
};

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function normalizePhoto(v: unknown): PlacementPhoto {
  return v === 'colony' ? 'colony' : 'frag';
}

function normalizePlacement(raw: unknown): Placement | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = str(o['id']);
  const coralId = str(o['coralId']);
  if (id === '' || coralId === '') return null;
  return {
    id,
    coralId,
    xPct: clamp(num(o['xPct'], 0.5), 0, 1),
    yPct: clamp(num(o['yPct'], 0.5), 0, 1),
    sizePct: clamp(num(o['sizePct'], DEFAULT_DOT_SIZE), MIN_DOT_SIZE, MAX_DOT_SIZE),
    photo: normalizePhoto(o['photo']),
  };
}

function normalizeAquascape(raw: unknown): Aquascape | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = str(o['id']);
  const src = str(o['src']);
  if (id === '' || src === '') return null;
  const built = o['builtIn'] === true;
  return {
    id,
    name: str(o['name'], 'Aquascape'),
    src,
    ...(built ? { builtIn: true } : {}),
  };
}

/** Defensively coerce an unknown persisted blob into a valid Workspace. */
export function normalizeWorkspace(raw: unknown): Workspace {
  if (!raw || typeof raw !== 'object') return DEFAULT_WORKSPACE;
  const o = raw as Record<string, unknown>;

  const aquascapesRaw = Array.isArray(o['aquascapes']) ? o['aquascapes'] : [];
  const aquascapes: Aquascape[] = [];
  for (const a of aquascapesRaw) {
    const n = normalizeAquascape(a);
    if (n) aquascapes.push(n);
  }
  const placementsRaw =
    o['placements'] && typeof o['placements'] === 'object'
      ? (o['placements'] as Record<string, unknown>)
      : {};
  const placements: Record<string, Placement[]> = {};
  for (const key of Object.keys(placementsRaw)) {
    const list = placementsRaw[key];
    if (!Array.isArray(list)) continue;
    const cleaned: Placement[] = [];
    for (const p of list) {
      const n = normalizePlacement(p);
      if (n) cleaned.push(n);
    }
    placements[key] = cleaned;
  }

  const activeId = str(o['activeAquascapeId']);
  const active = aquascapes.some((a) => a.id === activeId)
    ? activeId
    : (aquascapes[0]?.id ?? '');

  return {
    version: 1,
    aquascapes,
    activeAquascapeId: active,
    placements,
    defaultDotSize: clamp(
      num(o['defaultDotSize'], DEFAULT_DOT_SIZE),
      MIN_DOT_SIZE,
      MAX_DOT_SIZE,
    ),
  };
}

/** Defensively coerce an unknown persisted blob into a valid CoralEdits map. */
export function normalizeEdits(raw: unknown): CoralEdits {
  if (!raw || typeof raw !== 'object') return {};
  const o = raw as Record<string, unknown>;
  const out: CoralEdits = {};
  for (const id of Object.keys(o)) {
    const e = o[id];
    if (!e || typeof e !== 'object') continue;
    const eo = e as Record<string, unknown>;
    const edit: {
      name?: string;
      species?: string;
      colors?: ColorTag[];
      growthForm?: GrowthForm;
      bodyColor?: string;
      polypColor?: string;
      tsaUrl?: string;
    } = {};
    if (typeof eo['name'] === 'string') edit.name = eo['name'];
    if (typeof eo['species'] === 'string') edit.species = eo['species'];
    if (typeof eo['bodyColor'] === 'string') edit.bodyColor = eo['bodyColor'];
    if (typeof eo['polypColor'] === 'string') edit.polypColor = eo['polypColor'];
    if (typeof eo['tsaUrl'] === 'string') edit.tsaUrl = eo['tsaUrl'];
    if (isGrowthForm(eo['growthForm'])) edit.growthForm = eo['growthForm'];
    if (Array.isArray(eo['colors'])) {
      const colors = eo['colors'].filter(isColorTag) as ColorTag[];
      if (colors.length > 0) edit.colors = colors;
    }
    out[id] = edit;
  }
  return out;
}

// ── Pure workspace mutations (immutable) ────────────────────────────

export function placementsFor(ws: Workspace, aquascapeId: string): readonly Placement[] {
  return ws.placements[aquascapeId] ?? [];
}

export function setPlacementsFor(
  ws: Workspace,
  aquascapeId: string,
  next: readonly Placement[],
): Workspace {
  return {
    ...ws,
    placements: { ...ws.placements, [aquascapeId]: [...next] },
  };
}

export function addPlacement(
  ws: Workspace,
  aquascapeId: string,
  placement: Placement,
): Workspace {
  return setPlacementsFor(ws, aquascapeId, [
    ...placementsFor(ws, aquascapeId),
    placement,
  ]);
}

export function updatePlacement(
  ws: Workspace,
  aquascapeId: string,
  id: string,
  patch: Partial<Omit<Placement, 'id'>>,
): Workspace {
  const next = placementsFor(ws, aquascapeId).map((p) =>
    p.id === id ? { ...p, ...patch } : p,
  );
  return setPlacementsFor(ws, aquascapeId, next);
}

export function removePlacement(
  ws: Workspace,
  aquascapeId: string,
  id: string,
): Workspace {
  const next = placementsFor(ws, aquascapeId).filter((p) => p.id !== id);
  return setPlacementsFor(ws, aquascapeId, next);
}

export function clearPlacements(ws: Workspace, aquascapeId: string): Workspace {
  return setPlacementsFor(ws, aquascapeId, []);
}

export function setActiveAquascape(ws: Workspace, id: string): Workspace {
  if (!ws.aquascapes.some((a) => a.id === id)) return ws;
  return { ...ws, activeAquascapeId: id };
}

export function addAquascape(ws: Workspace, aquascape: Aquascape): Workspace {
  return {
    ...ws,
    aquascapes: [...ws.aquascapes, aquascape],
    activeAquascapeId: aquascape.id,
  };
}

export function renameAquascape(
  ws: Workspace,
  id: string,
  name: string,
): Workspace {
  return {
    ...ws,
    aquascapes: ws.aquascapes.map((a) => (a.id === id ? { ...a, name } : a)),
  };
}

export function removeAquascape(ws: Workspace, id: string): Workspace {
  const target = ws.aquascapes.find((a) => a.id === id);
  if (!target || target.builtIn) return ws;
  const aquascapes = ws.aquascapes.filter((a) => a.id !== id);
  const { [id]: _removed, ...placements } = ws.placements;
  void _removed;
  const active =
    ws.activeAquascapeId === id
      ? (aquascapes[0]?.id ?? '')
      : ws.activeAquascapeId;
  return { ...ws, aquascapes, placements, activeAquascapeId: active };
}

export function setDefaultDotSize(ws: Workspace, size: number): Workspace {
  return { ...ws, defaultDotSize: clamp(size, MIN_DOT_SIZE, MAX_DOT_SIZE) };
}

// ── Pure edits mutations ────────────────────────────────────────────

export function setCoralEdit(
  edits: CoralEdits,
  id: string,
  edit: CoralEdit,
): CoralEdits {
  return { ...edits, [id]: edit };
}

export function clearCoralEdit(edits: CoralEdits, id: string): CoralEdits {
  const { [id]: _removed, ...rest } = edits;
  void _removed;
  return rest;
}

export { clamp as clampNumber };
